import type { Analysis } from '@ni/shared';
import {
  analysisCaveats,
  analysisOfKind,
  analysisProse,
  relationshipLists,
  wealthFitRows,
} from '../../lib/analysis-copy';
import {
  WEALTH_FIT_ANGLES_LABEL,
  WEALTH_FIT_HEAD,
  WEALTH_FIT_SUB,
  type StatusForAnnotation,
} from '../../lib/copy';
import { formatMonth } from '../../lib/format';
import { Caveats } from '../ui/caveats';
import { Prose } from '../ui/prose';
import { KeptFromEarlier } from './kept-from-earlier';
import { LabelledList } from './labelled-list';
import { Section } from '../ui/section';

/**
 * **Fit and interest** — the wealth vertical's prospect read (`wealth_fit`,
 * wealth-vertical note §"The read"), and the third block in the reading column
 * beside Fit and Relationship.
 *
 * It is **composed rather than invented**: the rows are the fit grid the club's
 * block has rendered since it shipped, and the angles are the relationship
 * read's list with its caps. That is the whole reason the kind's output keys are
 * `summary`, `criteria`, `angles` and `caveats` — every one already on
 * `AnalysisContent`, so this page needed no shared shape to move and this
 * component needed no new reader.
 *
 * Built on the same rule as its two neighbours: **no analysis, no block.** There
 * is no empty state, no "not run yet", no placeholder — a club dossier does not
 * have this section and a wealth dossier does not have Fit, and neither page says
 * anything about the other's absence. Nothing here is gated on `org.vertical`
 * either: the presence of the analysis is the gate, which stays right for a
 * subject whose org changed its mind, and right for a prospect switched to client
 * whose read was written when the office was still deciding.
 *
 * **What the block does not do is render a verdict**, because the kind does not
 * write one. There is no score, no total, no "qualified" line and no sort by
 * met-ness: the rows are the dimensions in the order the reading put them, unmet
 * ones marked the way the club's grid marks them — amber, a tension to raise
 * rather than a finding against the person. The office decides; the read
 * describes. A regulated adviser's prospecting read that reached a verdict would
 * be a document their firm would rather not have written, and the page is the
 * last place that could have added one back.
 *
 * Labelled as interpretation where it sits among facts (ui-design §3.9), and it
 * closes with its own caveats rather than a shared bin: the hedging is about this
 * reading.
 */
export function WealthFitBlock({
  analyses,
  subject,
}: {
  analyses: Analysis[];
  /** The status block behind the kept-from-earlier line; absent, no line. */
  subject?: StatusForAnnotation;
}) {
  const fit = analysisOfKind(analyses, 'wealth_fit');
  if (!fit) return null;

  const rows = wealthFitRows(fit);
  /**
   * **The paragraph is rendered beside the rows, not instead of them**, and this
   * is the one place the block departs from the club's Fit.
   *
   * There the prose is a fallback shown only when the rows are missing, because
   * the club's reading *is* its criteria. Here the summary carries something the
   * rows cannot: capacity as the record shows it — the source of the wealth, the
   * events, what each rests on — which is a paragraph and not a dimension with a
   * tick beside it. Dropping it the moment a row appeared would throw away the
   * half of the read the office opened the page for.
   */
  const prose = analysisProse(fit);
  /* `angles` is the relationship read's key and this kind's fourth; the reader is
     that list's, so an analysis carrying `connections` too would simply not have
     it read here — this block is the fit rows and the angles, and a connections
     list belongs to the reading that owns it. */
  const { angles } = relationshipLists(fit);

  return (
    <Section lead title={WEALTH_FIT_HEAD}>
      <div className="sub">
        {WEALTH_FIT_SUB}
        {fit.as_of ? ` · as of ${formatMonth(fit.as_of)}` : ''}
      </div>
      <KeptFromEarlier subject={subject} kind="wealth_fit" />

      {prose ? <Prose text={prose} className="notes" /> : null}

      {rows.length > 0 ? (
        <div className="fit-grid">
          {/* Index-composed for the same reason the club's grid is: the criterion
              names come from the model, and two rows that happen to share one
              would collide as keys. */}
          {rows.map((row, index) => (
            <div
              className={row.met ? 'fit-row' : 'fit-row unmet'}
              key={`${index}-${row.criterion}`}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                aria-hidden="true"
              >
                {row.met ? (
                  <path d="M2 6.3 L4.7 9 L10 2.8" />
                ) : (
                  /* A cross at the check's own weight and box, so the two marks
                     sit on one optical line down the grid. Amber, not red: a
                     dimension the record shows tension with is a thing to raise,
                     not a finding against the person. */
                  <path d="M2.6 2.6 L9.4 9.4 M9.4 2.6 L2.6 9.4" />
                )}
              </svg>
              <div>
                <div className="criterion">{row.criterion}</div>
                {row.note ? <div className="note">{row.note}</div> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {angles.length > 0 ? (
        <div className="talk-lists">
          <LabelledList label={WEALTH_FIT_ANGLES_LABEL} lines={angles} />
        </div>
      ) : null}

      <Caveats caveats={analysisCaveats(fit)} />
    </Section>
  );
}
